import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Alert, Button, Form } from "react-bootstrap";
import {
  getGalleryFolderList,
  createGalleryFolder,
  deleteGalleryFolder,
  renameGalleryFolder,
} from "../../../api";
import "./manageFolders.css";

const ManageFolders = () => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newFolder, setNewFolder] = useState("");
  const [renameData, setRenameData] = useState({
    oldName: "",
    newName: "",
  });

  useEffect(() => {
    fetchFolders();
  }, []);

  /* =====================
     FETCH FOLDERS
  ====================== */
  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await getGalleryFolderList();

      if (Array.isArray(res.data)) {
        setFolders(res.data);
      } else {
        setError("Failed to load folders");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong while fetching folders");
    } finally {
      setLoading(false);
    }
  };

  /* =====================
     CREATE FOLDER
  ====================== */
  const handleCreateFolder = async () => {
    if (!newFolder.trim()) return;

    try {
      await createGalleryFolder(newFolder);
      setNewFolder("");
      fetchFolders();
    } catch (err) {
      alert("Failed to create folder");
    }
  };

  /* =====================
     DELETE FOLDER
  ====================== */
  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder}"?`)) return;

    try {
      await deleteGalleryFolder(folder);
      fetchFolders();
    } catch (err) {
      alert("Failed to delete folder");
    }
  };

  /* =====================
     RENAME FOLDER
  ====================== */
  const handleRenameFolder = async () => {
    if (!renameData.oldName || !renameData.newName) return;

    try {
      await renameGalleryFolder(
        renameData.oldName,
        renameData.newName
      );
      setRenameData({ oldName: "", newName: "" });
      fetchFolders();
    } catch (err) {
      alert("Failed to rename folder");
    }
  };

  return (
    <section className="manage-folders-page">
      <Container>
        <h2 className="page-title">Manage Gallery Folders</h2>
        <p className="page-subtitle">
          Create, rename and delete gallery folders
        </p>

        {/* CREATE FOLDER */}
        <div className="folder-action-box">
          <Form.Control
            type="text"
            placeholder="Enter new folder name"
            value={newFolder}
            onChange={(e) => setNewFolder(e.target.value)}
          />
          <Button onClick={handleCreateFolder}>
            Create Folder
          </Button>
        </div>

        {/* RENAME FOLDER */}
        <div className="folder-action-box">
          <Form.Select
            value={renameData.oldName}
            onChange={(e) =>
              setRenameData({ ...renameData, oldName: e.target.value })
            }
          >
            <option value="">Select folder</option>
            {folders.map((f, i) => (
              <option key={i} value={f.name}>
                {f.name}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="text"
            placeholder="New folder name"
            value={renameData.newName}
            onChange={(e) =>
              setRenameData({ ...renameData, newName: e.target.value })
            }
          />

          <Button variant="warning" onClick={handleRenameFolder}>
            Rename
          </Button>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {/* EMPTY */}
        {!loading && !error && folders.length === 0 && (
          <Alert variant="info">No folders found</Alert>
        )}

        {/* TABLE */}
        {!loading && folders.length > 0 && (
          <div className="table-responsive">
            <Table bordered hover className="folders-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Folder Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {folders.map((folder, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{folder.name}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteFolder(folder.name)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    </section>
  );
};

export default ManageFolders;
